import React from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle,
  FileText,
  FolderOpen,
  Plus,
  Settings,
} from "lucide-react";

function ActionButton({ children, onClick, primary = false, icon: Icon }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "inline-flex items-center justify-center gap-2 rounded-sm px-3 py-2 text-sm font-medium",
        primary
          ? "bg-[#0f6cbd] text-white hover:bg-[#115ea3]"
          : "border border-[#edebe9] bg-white text-[#323130] hover:bg-[#f3f2f1]",
      ].join(" ")}
    >
      {Icon && <Icon size={16} />}
      {children}
    </button>
  );
}

function SectionCard({ title, action, children }) {
  return (
    <section className="overflow-hidden rounded-sm border border-[#edebe9] bg-white">
      <div className="flex items-center justify-between border-b border-[#edebe9] bg-[#faf9f8] px-4 py-2">
        <h2 className="text-sm font-semibold text-[#201f1e]">{title}</h2>
        {action}
      </div>

      <div>{children}</div>
    </section>
  );
}

function AttentionRow({ icon: Icon, title, meta, tone = "default", onClick }) {
  const toneClass =
    tone === "warning"
      ? "bg-[#fff4ce] hover:bg-[#fff0b3]"
      : "bg-white hover:bg-[#faf9f8]";

  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "flex w-full items-center justify-between gap-4 border-b border-[#edebe9] px-4 py-3 text-left last:border-b-0",
        toneClass,
      ].join(" ")}
    >
      <div className="flex min-w-0 items-center gap-3">
        {Icon && <Icon size={17} className="shrink-0 text-[#605e5c]" />}

        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-[#201f1e]">
            {title}
          </div>
          {meta && <div className="mt-0.5 text-xs text-[#605e5c]">{meta}</div>}
        </div>
      </div>

      <span className="text-xs text-[#8a8886]">Open</span>
    </button>
  );
}

function QuoteRow({ title, status, amount, onClick, warning = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center justify-between gap-4 border-b border-[#edebe9] px-4 py-3 text-left last:border-b-0 hover:bg-[#faf9f8]"
    >
      <div className="min-w-0">
        <div className="truncate text-sm font-medium text-[#201f1e]">
          {title}
        </div>

        <div className="mt-1 flex items-center gap-2 text-xs">
          <span
            className={[
              "rounded-sm px-1.5 py-0.5 font-medium",
              warning
                ? "bg-[#fff4ce] text-[#8a6d1d]"
                : "bg-[#f3f2f1] text-[#605e5c]",
            ].join(" ")}
          >
            {status}
          </span>
        </div>
      </div>

      <div className="shrink-0 text-sm font-semibold tabular-nums text-[#201f1e]">
        {amount}
      </div>
    </button>
  );
}

function NavTile({ children, onClick, icon: Icon }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-2 rounded-sm border border-[#edebe9] bg-white px-3 py-2 text-sm text-[#323130] hover:bg-[#f3f2f1]"
    >
      {Icon && <Icon size={16} className="text-[#605e5c]" />}
      {children}
    </button>
  );
}

export default function Overview() {
  const navigate = useNavigate();

  return (
    <div className="min-h-full bg-[#f3f2f1] p-6 text-[#201f1e]">
      <div className="mx-auto max-w-6xl space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Overview</h1>
          </div>

          <div className="flex flex-wrap gap-2">
            <ActionButton
              primary
              icon={Plus}
              onClick={() => navigate("/quotes")}
            >
              New quote
            </ActionButton>

            <ActionButton onClick={() => navigate("/quotes-list")}>
              Quote list
            </ActionButton>
          </div>
        </div>

        <div className="grid gap-5 xl:grid-cols-[1fr_340px]">
          <div className="space-y-5">
            <SectionCard title="Needs attention">
              <AttentionRow
                icon={AlertTriangle}
                title="3 quotes need review"
                meta="Low confidence or missing pricing"
                tone="warning"
                onClick={() => navigate("/quotes-list")}
              />

              <AttentionRow
                icon={FolderOpen}
                title="2 projects awaiting action"
                meta="Follow-up or status update"
                onClick={() => navigate("/projects")}
              />

              <AttentionRow
                icon={FileText}
                title="Recent quotes ready"
                meta="Preview without leaving the list"
                onClick={() => navigate("/quotes-list")}
              />
            </SectionCard>

            <SectionCard
              title="Recent quotes"
              action={
                <button
                  type="button"
                  onClick={() => navigate("/quotes-list")}
                  className="rounded-sm px-2 py-1 text-xs text-[#0f6cbd] hover:bg-[#f3f2f1]"
                >
                  View all
                </button>
              }
            >
              <QuoteRow
                title="Internal wall with insulation"
                status="Draft"
                amount="42.500 kr."
                onClick={() => navigate("/quotes-list")}
              />

              <QuoteRow
                title="Ceiling and wall lining"
                status="Needs review"
                amount="68.200 kr."
                warning
                onClick={() => navigate("/quotes-list")}
              />

              <QuoteRow
                title="Terrace estimate"
                status="Saved"
                amount="91.000 kr."
                onClick={() => navigate("/quotes-list")}
              />
            </SectionCard>
          </div>

          <div className="space-y-5">
            <SectionCard title="Snapshot">
              <div className="grid grid-cols-2 gap-px bg-[#edebe9]">
                <Metric label="Review" value="3" warning />
                <Metric label="Projects" value="7" />
                <Metric label="Quotes" value="12" />
                <Metric label="Approved" value="8" success />
              </div>
            </SectionCard>

            <SectionCard title="Go to">
              <div className="grid gap-2 p-4">
                <NavTile icon={FolderOpen} onClick={() => navigate("/projects")}>
                  Projects
                </NavTile>

                <NavTile icon={FileText} onClick={() => navigate("/todo")}>
                  To-do
                </NavTile>

                <NavTile icon={CheckCircle} onClick={() => navigate("/economy")}>
                  Economy
                </NavTile>

                <NavTile icon={Settings} onClick={() => navigate("/settings")}>
                  Settings
                </NavTile>
              </div>
            </SectionCard>
          </div>
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, warning = false, success = false }) {
  return (
    <div className="bg-white p-4">
      <div
        className={[
          "text-2xl font-semibold tabular-nums",
          warning ? "text-[#8a6d1d]" : "",
          success ? "text-[#107c10]" : "",
          !warning && !success ? "text-[#201f1e]" : "",
        ].join(" ")}
      >
        {value}
      </div>

      <div className="mt-1 text-xs font-medium uppercase tracking-wide text-[#605e5c]">
        {label}
      </div>
    </div>
  );
}